import axiosInstance from "./httpCommon3";
import lookupAxios from "./httpCommon3";
import newAxios from "./httpCommonNew";

// Pathology ? 1 : Radiology ? 2 : Both ? 3
export const DIAGNOSTIC_CATEGORY_IDS = {
  PATHOLOGY: 1,
  RADIOLOGY: 2,
  BOTH: 3,
};

export const getDiagnosticCategoryId = (diagnosticType = "") => {
  if (typeof diagnosticType === "number") return diagnosticType;
  const type = String(diagnosticType).trim().toUpperCase();
  if (type === "PATHOLOGY") return DIAGNOSTIC_CATEGORY_IDS.PATHOLOGY;
  if (type === "RADIOLOGY") return DIAGNOSTIC_CATEGORY_IDS.RADIOLOGY;
  if (type === "BOTH") return DIAGNOSTIC_CATEGORY_IDS.BOTH;
  return undefined;
};

export const CC_ONBOARDING_BASE = "/collection-center/onboarding";

export const STATUS_CODES = {
  DRAFT: 10,
  PENDING_APPROVAL: 11,
  PARTIALLY_APPROVED: 12,
  APPROVED: 13,
  REJECTED: 14,
};

export const STATUS_LABELS = {
  [STATUS_CODES.DRAFT]: "Draft",
  [STATUS_CODES.PENDING_APPROVAL]: "Waiting for Approval",
  [STATUS_CODES.PARTIALLY_APPROVED]: "Partially Approved",
  [STATUS_CODES.APPROVED]: "Approved",
  [STATUS_CODES.REJECTED]: "Rejected",
};

export const USER_ROLE_OPTIONS = [
  { id: "12", value: "Swasthya Mitra Diagnostic", name: "Swasthya Mitra Diagnostic" },
  { id: "13", value: "Phlebo – Freelancer", name: "Phlebo – Freelancer" },
  { id: "14", value: "Cooperate Diagnostics", name: "Cooperate Diagnostics" },
];

// The option values above are LABELS, so anything else the API sends selects
// nothing and the required field renders as "Select User Role" on a record that
// plainly has a role — which then gets saved back as blank.
//
// Three different spellings of this one field exist in live data: the label (what
// the wizard posts), the role code (USER.ROLECODE, e.g. "SWASTH"), and — in
// drafts saved before this was fixed — the code again, because the screen read it
// off the detail response and saved it. resolveUserRole accepts all of them.
//
// Only Swasthya Mitra has a role code. Phlebo and Corporate create no USER row,
// so there is no code to map and they are matched by label alone.
export const USER_ROLE_CODE_TO_LABEL = {
  SWASTH: "Swasthya Mitra Diagnostic",
  // "Cooperate" is the spelling the option value and the COOPERATE_CODE column
  // use; "Corporate" is the correct English and is what the admin module and
  // half the log messages say. Both appear in stored data, so both resolve.
  CORPORATEDIAGNOSTICS: "Cooperate Diagnostics",
  COOPERATEDIAGNOSTICS: "Cooperate Diagnostics",
};

// Letters and digits only, lower-cased. Absorbs the punctuation that differs
// between the places this label is typed: "Phlebo – Freelancer" (en-dash, as
// above), "Phlebo-Freelancer" (hyphen) and "Phlebo–Freelancer" (no spaces) are
// all the same role, and an ASCII hyphen where the option has U+2013 is exactly
// the kind of mismatch that is invisible in a diff.
const lettersOnly = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/**
 * The exact USER_ROLE_OPTIONS value for a label, a role code, or a near miss.
 *
 * Returns "" for anything unrecognised so the select falls back to its
 * placeholder rather than holding a value no option carries.
 */
export function resolveUserRole(value) {
  if (value === null || value === undefined || value === "") return "";

  // USERROLES can grant several; the first one recognised wins.
  for (const part of String(value).split(",")) {
    const candidate = part.trim();
    if (!candidate) continue;

    const byCode = USER_ROLE_CODE_TO_LABEL[candidate.toUpperCase()];
    if (byCode) return byCode;

    const exact = USER_ROLE_OPTIONS.find((r) => r.value === candidate);
    if (exact) return exact.value;

    const loose = lettersOnly(candidate);
    const fuzzy = USER_ROLE_OPTIONS.find((r) => lettersOnly(r.value) === loose);
    if (fuzzy) return fuzzy.value;
  }
  return "";
}

// Pathology = 1, Radiology = 2, Both = 3 — the UI state and the API both
// use these numeric codes directly now (no more "Pathology"/"Radiology"/
// "Both" strings on the wire).
export const DIAGNOSTIC_TYPE_OPTIONS = [
  { value: 1, label: "Pathology" },
  { value: 2, label: "Radiology" },
  { value: 3, label: "Both" },
];

// Shared Yes/No radio option set for automapping and "Is CureBay CC
// Center?" — Yes = 1, No = 0, sent to the API as-is.
export const YES_NO_OPTIONS = [
  { value: 1, label: "Yes" },
  { value: 0, label: "No" },
];

// Per the current Postman collection the flow is exactly 3 users:
// 1 Creator + 2 Approvers (Business Head -> Finance Head). Both approver
// levels share statusCode 11/13 — only approverLevel tells them apart,
// and only the Finance Head (level 2, the last level) makes a 13 call
// that actually onboards the record / publishes to MDM.
export const TOTAL_APPROVAL_LEVELS = 2;

export const APPROVER_LEVEL_LABELS = {
  1: "Business Head",
  2: "Finance Head",
};

/**
 * Who is acting, from the login the rest of the app already stores.
 *
 * This replaces a hardcoded CURRENT_USERS array of three test identities — a
 * creator and two approvers, with real user codes baked into the source. It was
 * never actually imported anywhere, so nothing depended on it, but leaving it
 * invited someone to wire it up and ship a build that acted as whichever person
 * happened to be listed second.
 *
 * The lookup follows the app-wide convention: `loginObj.userSession.userCode`,
 * falling back to `access_name`. That is what ccBulkUpload, mappingList,
 * mobilabUserList and the rest all do, and it is the code the APIs expect.
 * `loginObj.user.code` also exists on the stored object and is NOT the same field
 * — it is used for the menu-permission lookup, and reaching for it here is how the
 * workflow screen ended up sending a different userCode from the wizard.
 */
export function currentUserCode() {
  try {
    const loginObj = JSON.parse(localStorage.getItem("loginObj"));
    return loginObj?.userSession?.userCode || localStorage.getItem("access_name") || "";
  } catch {
    return localStorage.getItem("access_name") || "";
  }
}

/** The acting user's display name, for the header. Falls back to their code. */
export function currentUserName() {
  try {
    const loginObj = JSON.parse(localStorage.getItem("loginObj"));
    return loginObj?.user?.firstName || currentUserCode();
  } catch {
    return currentUserCode();
  }
}

function qs(params = {}) {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      usp.set(key, value);
    }
  });
  const s = usp.toString();
  return s ? `?${s}` : "";
}

const ccOnboardingApi = {
  saveStep: ({ stepNo, draftKey, centerCode, userCode, onboardingType = "CC", complete = true, body }) =>
    axiosInstance.post(
      `${CC_ONBOARDING_BASE}/draft/step/${stepNo}${qs({ draftKey, centerCode, userCode, onboardingType, complete })}`,
      body
    ),

  // Ordered list of approval levels (e.g. Business L1, Business L2,
  // Finance L3) used to show "level X of Y" on the success screen.
  //
  // The path is /workflow/chain — it was /workflow-chain here, which 404s, so
  // every "level X of Y" silently fell back to the hardcoded
  // TOTAL_APPROVAL_LEVELS below. Read totalLevels from this response instead of
  // the constant wherever you can: adding an approver is an INSERT on the
  // backend, and this endpoint is what renumbers.
  getWorkflowChain: () => axiosInstance.get(`${CC_ONBOARDING_BASE}/workflow/chain`),

  // The approver's own queue. Answers what /list cannot: which level each
  // record waits at, WHO it waits on (pendingWithUserCode/Name), daysWaiting,
  // and per-level counts for the tabs. Already scoped to userCode.
  getPending: ({ onboardingType = "CC", role, userCode } = {}) =>
    axiosInstance.get(`${CC_ONBOARDING_BASE}/pending${qs({ onboardingType, role, userCode })}`),

  getDraft: (draftKey) => axiosInstance.get(`${CC_ONBOARDING_BASE}/draft/${draftKey}`),

  // First-time submit: no centre code exists yet, one is generated by the
  // backend. This is its own endpoint — NOT a /status?statusCode=11 call
  // (that shape is reserved for resubmitting a rejected record).
  submitDraft: ({ draftKey, userCode }) =>
    axiosInstance.post(`${CC_ONBOARDING_BASE}/draft/${draftKey}/submit${qs({ userCode })}`),

  getDraftStep: (draftKey, stepNo) => axiosInstance.get(`${CC_ONBOARDING_BASE}/draft/${draftKey}/step/${stepNo}`),

  getDraftProgress: (draftKey) => axiosInstance.get(`${CC_ONBOARDING_BASE}/draft/${draftKey}/progress`),

  getDraftAudit: (draftKey) => axiosInstance.get(`${CC_ONBOARDING_BASE}/draft/${draftKey}/audit`),

  // userCode is not optional in practice: without it the queue returns every
  // user's drafts and every user's rejected facilities, not the caller's.
  getWorkQueue: ({ onboardingType = "CC", userCode, page = 0, size = 25 } = {}) =>
    axiosInstance.get(`${CC_ONBOARDING_BASE}/draft/work-queue${qs({ onboardingType, userCode, page, size })}`),

  changeStatus: ({ centerCode = "", statusCode, draftKey, userCode, remarks }) =>
    axiosInstance.post(
      `${CC_ONBOARDING_BASE}/${centerCode}/status${qs({ statusCode, draftKey, userCode, remarks })}`
    ),

  // Editing an ONBOARDED facility does NOT write anything. The draft is parked
  // and the live rows keep their values until the final approval level signs
  // off, so after this call the Detail tabs are unchanged and only `pendingEdit`
  // appears. Editing a facility that is not yet onboarded still materialises
  // immediately — same call, the backend decides by the record's status.
  update: ({ centerCode, draftKey, userCode, payload, form }) =>
    axiosInstance.put(
      `${CC_ONBOARDING_BASE}/${centerCode}${qs({ draftKey, userCode })}`,
      payload || form || {}
    ),

  // An approver pulls an already-live facility back to its submitter. Distinct
  // from reject, which only acts on something still awaiting approval. Does NOT
  // take the facility offline — it stays live until the correction is approved.
  sendBack: ({ centerCode, userCode, remarks }) =>
    axiosInstance.post(`${CC_ONBOARDING_BASE}/${centerCode}/send-back${qs({ userCode, remarks })}`),

  // Resubmit a rejected record: POST /{centerCode}/status?statusCode=11&draftKey={draftKey}&userCode={userCode}
  resubmit: ({ centerCode, draftKey, userCode, remarks }) =>
    axiosInstance.post(
      `${CC_ONBOARDING_BASE}/${centerCode}/status${qs({
        statusCode: STATUS_CODES.PENDING_APPROVAL, // 11
        draftKey,
        userCode: userCode || currentUserCode(),
        remarks,
      })}`
    ),

  // NOTE: every filter the screens expose must be forwarded as its own
  // query param — the API does its own (case-insensitive, "contains")
  // filtering server-side; it is not just a display hint. `eclinic` is
  // the API's actual param name (the UI/local filter state calls it
  // `eclinicCode` — mapped at the call site). `search` is the catch-all
  // box across ccName/smName/smNumber/eclinic. `userCode` scopes the
  // list to the logged-in user (see `scope` in the list response) and
  getList: ({
    onboardingType = "CC",
    statusCode,
    approverLevel,
    city,
    state,
    eclinic,
    ccName,
    labName,
    smName,
    smNumber,
    contactName,
    contactNumber,
    diagnosticType,
    diagnosticCategory,
    diagnosticCategoryId,
    createdDate,
    fromDate,
    toDate,
    search,
    page = 0,
    size = 25,
    sortDir = "desc",
  } = {}) =>
    axiosInstance.get(
      `${CC_ONBOARDING_BASE}/list${qs({
        onboardingType,
        statusCode,
        approverLevel,
        city,
        state,
        eclinic,
        ccName: ccName || labName,
        smName: smName || contactName,
        smNumber: smNumber || contactNumber,
        diagnosticType: diagnosticType || diagnosticCategory,
        diagnosticCategory: diagnosticCategory || diagnosticType,
        diagnosticCategoryId,
        createdDate,
        fromDate,
        toDate,
        search,
        page,
        size,
        sortDir,
      })}`
    ),

  getDetail: (centerCode) => axiosInstance.get(`${CC_ONBOARDING_BASE}/${centerCode}/detail`),

  getChangeLog: (centerCode, { fieldName, changedBy, fromDate, toDate, page = 0, size = 50 } = {}) =>
    axiosInstance.get(
      `${CC_ONBOARDING_BASE}/${centerCode}/change-log${qs({ fieldName, changedBy, fromDate, toDate, page, size })}`
    ),

  // Attachments — Draft (by draftKey and stepNo)
  uploadDraftAttachment: async ({
    draftKey,
    stepNo,
    onboardingType = "CC",
    userCode,
    description,
    labPartnerName,
    file,
    fileName,
    contentType,
    base64,
  }) => {
    if (!draftKey) throw new Error("draftKey is required to upload draft attachment");
    const uCode = userCode || currentUserCode();
    let b64 = base64;
    let name = fileName;
    let type = contentType;

    if (file && file instanceof File) {
      b64 = await toBase64(file);
      name = fileName || file.name;
      type = contentType || file.type || "application/octet-stream";
    }

    if (!b64) return null;

    return axiosInstance.post(
      `${CC_ONBOARDING_BASE}/draft/${draftKey}/attachment${qs({
        onboardingType,
        stepNo,
        userCode: uCode,
      })}`,
      {
        description,
        labPartnerName: labPartnerName || undefined,
        fileName: name,
        contentType: type,
        base64: b64,
      }
    );
  },

  listDraftAttachments: ({ draftKey, labPartnerName } = {}) =>
    axiosInstance.get(`${CC_ONBOARDING_BASE}/draft/${draftKey}/attachment${qs({ labPartnerName })}`),

  deleteDraftAttachment: ({ draftKey, attachmentId, userCode } = {}) =>
    axiosInstance.delete(
      `${CC_ONBOARDING_BASE}/draft/${draftKey}/attachment/${attachmentId}${qs({
        userCode: userCode || currentUserCode(),
      })}`
    ),

  queueDeleteLiveAttachment: ({ draftKey, attachmentId, centerCode, userCode } = {}) =>
    axiosInstance.delete(
      `${CC_ONBOARDING_BASE}/draft/${draftKey}/attachment/live/${attachmentId}${qs({
        centerCode,
        userCode: userCode || currentUserCode(),
      })}`
    ),

  // Attachments — Live Facility (by centerCode)
  uploadAttachment: async ({
    centerCode,
    userCode,
    onboardingType = "CC",
    description,
    labPartnerName,
    file,
    fileName,
    contentType,
    base64,
  }) => {
    if (!centerCode) throw new Error("centerCode is required to upload attachment");
    const uCode = userCode || currentUserCode();
    let b64 = base64;
    let name = fileName;
    let type = contentType;

    if (file && file instanceof File) {
      b64 = await toBase64(file);
      name = fileName || file.name;
      type = contentType || file.type || "application/octet-stream";
    }

    if (!b64) return null;

    return axiosInstance.post(
      `${CC_ONBOARDING_BASE}/${centerCode}/attachment${qs({ userCode: uCode })}`,
      {
        onboardingType,
        description,
        labPartnerName: labPartnerName || undefined,
        fileName: name,
        contentType: type,
        base64: b64,
      }
    );
  },

  listLiveAttachments: ({ centerCode, labPartnerName } = {}) =>
    axiosInstance.get(`${CC_ONBOARDING_BASE}/${centerCode}/attachment${qs({ labPartnerName })}`),

  deleteAttachment: ({ id, attachmentId, userCode } = {}) =>
    axiosInstance.delete(
      `${CC_ONBOARDING_BASE}/attachment/${attachmentId || id}${qs({ userCode: userCode || currentUserCode() })}`
    ),

  approve: ({ centerCode, userCode, remarks } = {}) =>
    axiosInstance.post(
      `${CC_ONBOARDING_BASE}/${centerCode}/approve${qs({ userCode: userCode || currentUserCode(), remarks })}`
    ),

  reject: ({ centerCode, userCode, remarks } = {}) =>
    axiosInstance.post(
      `${CC_ONBOARDING_BASE}/${centerCode}/reject${qs({ userCode: userCode || currentUserCode(), remarks })}`
    ),

  // ECLINIC dropdown — https://new-che-stage.curebay.in/dhp/location/getAllEclinic (REACT_APP_NEW base)
  getEclinicDropdown: () => newAxios.get(`location/getAllEclinic`),

  getCorporateDropdown: ({ state, search = "", corporateCode = "" } = {}) =>
    lookupAxios.get(`/dropdowns/cooperate${qs({ state, search, corporateCode })}`),

  getLabListByType: (diagnosticType) => lookupAxios.get(`/onboarding/getLabList${qs({ diagnosticType })}`),

  // Preferred lab — https://diagnostic-new-stage.curebay.in/diagnostic/api/nearLab/list
  // POST body: { locationPincode, diagnosticCategoryId } — Pathology:1, Radiology:2, Both:3
  getNearLabList: ({ pinCode, diagnosticType, diagnosticCategoryId } = {}) =>
    lookupAxios.post(`/api/nearLab/list`, {
      locationPincode: pinCode || "",
      diagnosticCategoryId: diagnosticCategoryId ?? getDiagnosticCategoryId(diagnosticType),
    }),

  getCommissionGradeList: (gradeDescription = "") =>
    lookupAxios.get(`/onboarding/getCommissionGradeList${qs({ gradeDescription })}`),

  // State dropdown list
  getStateList: () => lookupAxios.get(`/onboarding/getStateList`),
  getStatelist: () => lookupAxios.get(`/onboarding/getStateList`),

  // Zone and Circle dropdowns
  getZoneList: async () => {
    try {
      const res = await axiosInstance.get(`/dropdowns/zones`);
      if (res?.data) return res;
    } catch {
      // fallback
    }
    return lookupAxios.get(`/Zone/list`);
  },

  getCircleList: async (params = {}) => {
    let zoneVal = "";
    if (typeof params === "string") {
      zoneVal = params;
    } else if (typeof params === "object") {
      zoneVal = params?.zone || params?.zoneId || params?.zoneCode || "";
    }
    try {
      const res = await axiosInstance.get(`/dropdowns/circles${qs({ zone: zoneVal })}`);
      if (res?.data) return res;
    } catch {
      // fallback
    }
    return lookupAxios.get(`/CircleMaster/list/filter${qs({ zoneId: zoneVal })}`);
  },
};

// Stable slot constants matching backend storage routing and superseding
export const ATTACHMENT_SLOTS = {
  SM_PHOTO: "SM Photo",
  AGREEMENT_COPY: "Agreement Copy",
  CANCELLED_CHEQUE: "Cancelled Cheque",
  CC_LOGO: "CC Logo",
  AADHAAR_KYC: "Aadhaar KYC",
  PAN_CARD: "PAN Card",
  GST_CERTIFICATE: "GST Certificate",
  NABL_CERTIFICATE: "NABL Certificate",
  LAB_LOGO: "Lab Logo",
  KYC_DOCUMENT: "KYC Document",
  LAB_LICENSE: "Lab License",
};

export const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    if (!file) return resolve(null);
    if (typeof file === "string") return resolve(file);
    const r = new FileReader();
    r.onload = () => resolve(r.result); // "data:image/png;base64,iVBORw0KG..."
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export async function uploadFormAttachments({ centerCode, form, onboardingType = "CC", userCode }) {
  if (!centerCode || !form) return [];
  const uCode = userCode || currentUserCode();
  const uploads = [];

  // 1. SM Photo
  const smContact = form.contacts?.[0];
  if (smContact?.smPhoto && smContact.smPhoto instanceof File) {
    uploads.push(
      ccOnboardingApi.uploadAttachment({
        centerCode,
        userCode: uCode,
        onboardingType,
        description: ATTACHMENT_SLOTS.SM_PHOTO,
        file: smContact.smPhoto,
      })
    );
  }

  // 2. Documents
  const docs = form.documents || {};
  if (docs.contractCopy && docs.contractCopy instanceof File) {
    uploads.push(
      ccOnboardingApi.uploadAttachment({
        centerCode,
        userCode: uCode,
        onboardingType,
        description: ATTACHMENT_SLOTS.AGREEMENT_COPY,
        file: docs.contractCopy,
      })
    );
  }
  if (docs.bankProof && docs.bankProof instanceof File) {
    uploads.push(
      ccOnboardingApi.uploadAttachment({
        centerCode,
        userCode: uCode,
        onboardingType,
        description: ATTACHMENT_SLOTS.CANCELLED_CHEQUE,
        file: docs.bankProof,
      })
    );
  }
  if (docs.labLogo && docs.labLogo instanceof File) {
    uploads.push(
      ccOnboardingApi.uploadAttachment({
        centerCode,
        userCode: uCode,
        onboardingType,
        description: onboardingType === "LAB" ? ATTACHMENT_SLOTS.LAB_LOGO : ATTACHMENT_SLOTS.CC_LOGO,
        file: docs.labLogo,
      })
    );
  }
  if (docs.kycDocument && docs.kycDocument instanceof File) {
    uploads.push(
      ccOnboardingApi.uploadAttachment({
        centerCode,
        userCode: uCode,
        onboardingType,
        description: ATTACHMENT_SLOTS.AADHAAR_KYC,
        file: docs.kycDocument,
      })
    );
  }
  if (docs.panCard && docs.panCard instanceof File) {
    uploads.push(
      ccOnboardingApi.uploadAttachment({
        centerCode,
        userCode: uCode,
        onboardingType,
        description: ATTACHMENT_SLOTS.PAN_CARD,
        file: docs.panCard,
      })
    );
  }

  // 3. Test Timings Attachments
  const testAttachments = form.testTimings?.attachments || [];
  testAttachments.forEach((a) => {
    if (a.file && a.file instanceof File) {
      uploads.push(
        ccOnboardingApi.uploadAttachment({
          centerCode,
          userCode: uCode,
          onboardingType,
          description: a.partnerName ? `Test Attachment - ${a.partnerName}` : "Test Attachment",
          file: a.file,
        })
      );
    }
  });

  return Promise.allSettled(uploads);
}

export async function uploadDraftFormAttachments({ draftKey, form, onboardingType = "CC", userCode, stepNo }) {
  if (!draftKey || !form) return [];
  const uCode = userCode || currentUserCode();
  const uploads = [];

  // Step 2: SM Photo
  if (!stepNo || stepNo === 2) {
    const smContact = form.contacts?.[0];
    if (smContact?.smPhoto && smContact.smPhoto instanceof File) {
      uploads.push(
        ccOnboardingApi
          .uploadDraftAttachment({
            draftKey,
            stepNo: 2,
            userCode: uCode,
            onboardingType,
            description: ATTACHMENT_SLOTS.SM_PHOTO,
            file: smContact.smPhoto,
          })
          .then((res) => {
            if (res?.data) {
              smContact.remoteSmPhoto = res.data.viewUrl || res.data.publicUrl || res.data.fileUrl;
              smContact.attachmentId = res.data.id;
            }
            return res;
          })
          .catch((err) => {
            console.warn("Upload draft SM Photo failed:", err);
            return null;
          })
      );
    }
  }

  // Step 3: Test Timings Attachments
  if (!stepNo || stepNo === 3) {
    const testAttachments = form.testTimings?.attachments || [];
    testAttachments.forEach((a) => {
      if (a.file && a.file instanceof File) {
        uploads.push(
          ccOnboardingApi
            .uploadDraftAttachment({
              draftKey,
              stepNo: 3,
              userCode: uCode,
              onboardingType,
              labPartnerName: a.partnerName || undefined,
              description: a.description || (a.partnerName ? `Test Attachment - ${a.partnerName}` : "Test Rate Card"),
              file: a.file,
            })
            .then((res) => {
              if (res?.data) {
                a.remoteFileUrl = res.data.viewUrl || res.data.publicUrl || res.data.fileUrl;
                a.remoteFileName = res.data.fileName || a.file.name;
                a.attachmentId = res.data.id;
              }
              return res;
            })
            .catch((err) => {
              console.warn("Upload draft test attachment failed:", err);
              return null;
            })
        );
      }
    });
  }

  // Step 7: Documents
  if (!stepNo || stepNo === 7) {
    const docs = form.documents || {};
    const docItems = [
      { key: "contractCopy", desc: ATTACHMENT_SLOTS.AGREEMENT_COPY },
      { key: "bankProof", desc: ATTACHMENT_SLOTS.CANCELLED_CHEQUE },
      { key: "labLogo", desc: onboardingType === "LAB" ? ATTACHMENT_SLOTS.LAB_LOGO : ATTACHMENT_SLOTS.CC_LOGO },
      { key: "kycDocument", desc: ATTACHMENT_SLOTS.AADHAAR_KYC },
      { key: "panCard", desc: ATTACHMENT_SLOTS.PAN_CARD },
      { key: "gstCertificate", desc: ATTACHMENT_SLOTS.GST_CERTIFICATE },
    ];

    docItems.forEach(({ key, desc }) => {
      const f = docs[key];
      if (f && f instanceof File) {
        uploads.push(
          ccOnboardingApi
            .uploadDraftAttachment({
              draftKey,
              stepNo: 7,
              userCode: uCode,
              onboardingType,
              description: desc,
              file: f,
            })
            .then((res) => {
              if (res?.data) {
                docs.remote = docs.remote || {};
                docs.remote[key] = res.data.viewUrl || res.data.publicUrl || res.data.fileUrl;
                docs.attachmentIds = docs.attachmentIds || [];
                if (res.data.id && !docs.attachmentIds.includes(res.data.id)) {
                  docs.attachmentIds.push(res.data.id);
                }
              }
              return res;
            })
            .catch((err) => {
              console.warn(`Upload draft document failed (${desc}):`, err);
              return null;
            })
        );
      }
    });
  }

  return Promise.allSettled(uploads);
}

// Alias to match the naming used elsewhere ("apiservice.getStatelist")
ccOnboardingApi.getStatelist = ccOnboardingApi.getStateList;

export default ccOnboardingApi;